import { useCallback, useMemo, useRef, useState } from "react";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { useGetOrderListQuery } from "../../../../api/endpoints/orders";
import { matchModeToOperator } from "../../../../utils/filterUtils";
import { formatDateForApi } from "../../../../utils/dateUtils";

const toBackendColumn = (field) => (field ? field.charAt(0).toUpperCase() + field.slice(1) : field);

const INITIAL_LAZY_PARAMS = { page: 1, rows: 10 };

const parseNumericInput = (raw) => {
  let text = String(raw).trim().replace(/\s/g, "");
  if (text === "") return null;

  const negative = text.startsWith("-");
  if (negative) text = text.slice(1);

  const separatorIndex = Math.max(text.lastIndexOf(","), text.lastIndexOf("."));

  if (separatorIndex > -1) {
    const decimalChar = text[separatorIndex];
    const groupChar = decimalChar === "," ? "." : ",";
    const decimalCharCount = text.split(decimalChar).length - 1;
    const fractionLength = text.length - separatorIndex - 1;

    if (decimalCharCount > 1 || fractionLength === 3) {
      text = text.replace(/[.,]/g, "");
    } else {
      const withoutGroups = text.split(groupChar).join("");
      const decimalIndex = withoutGroups.lastIndexOf(decimalChar);
      text = `${withoutGroups.slice(0, decimalIndex)}.${withoutGroups.slice(decimalIndex + 1)}`;
    }
  }

  if (text === "" || text === ".") return null;

  const parsed = Number(text);
  return Number.isFinite(parsed) ? (negative ? -parsed : parsed) : null;
};

const formatNumericValue = (raw) => {
  const parsed = parseNumericInput(raw);
  if (parsed === null) return String(raw).trim();
  return parsed.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatFilterValue = (value, numeric) => {
  if (value instanceof Date) return formatDateForApi(value);
  const text = String(value).trim();
  return numeric ? formatNumericValue(text) : text;
};

const hasFilterValue = (value) => {
  if (value === null || value === undefined) return false;
  if (value instanceof Date) return true;
  return String(value).trim() !== "";
};

const buildColumnFilters = (columnFilters) =>
  Object.entries(columnFilters || {})
    .filter(([, model]) => hasFilterValue(model?.value))
    .map(([field, model]) => ({
      column: toBackendColumn(field),
      operator: matchModeToOperator[model.matchMode],
      value: formatFilterValue(model.value, model.numeric)
    }))
    .filter((filter) => filter.operator);

const buildOrderListParams = ({ dateRange, filters, sortBy, sortDirection, pageNumber, pageSize }) => {
  const params = { pageNumber: String(pageNumber), pageSize: String(pageSize) };

  if (dateRange?.startDate) params.startDate = dateRange.startDate;
  if (dateRange?.endDate) params.endDate = dateRange.endDate;
  if (sortBy) params.sortBy = sortBy;
  if (sortDirection) params.sortDirection = sortDirection;

  filters.forEach((f, i) => {
    params[`filters[${i}].Column`] = f.column;
    params[`filters[${i}].Operator`] = String(f.operator);
    params[`filters[${i}].Value`] = f.value ?? "";
  });

  return params;
};

const useOrderList = () => {
  const { th } = useLanguage();
  const toastRef = useRef(null);

  const [lazyParams, setLazyParams] = useState(INITIAL_LAZY_PARAMS);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(0);

  const [appliedDateRange, setAppliedDateRange] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [filterResetToken, setFilterResetToken] = useState(0);

  const hasApplied = appliedDateRange !== null;

  const filters = useMemo(() => buildColumnFilters(columnFilters), [columnFilters]);

  const sortDirection = sortOrder === 1 ? "asc" : sortOrder === -1 ? "desc" : null;
  const sortBy = sortField ? toBackendColumn(sortField) : null;

  const queryParams = useMemo(
    () => buildOrderListParams({ dateRange: appliedDateRange, filters, sortBy, sortDirection, pageNumber: lazyParams.page, pageSize: lazyParams.rows }),
    [appliedDateRange, filters, sortBy, sortDirection, lazyParams.page, lazyParams.rows]
  );

  const exportParams = useMemo(
    () => buildOrderListParams({ dateRange: appliedDateRange, filters, sortBy, sortDirection, pageNumber: 1, pageSize: 0 }),
    [appliedDateRange, filters, sortBy, sortDirection]
  );

  const { data, isLoading, isFetching } = useGetOrderListQuery(queryParams, { skip: !hasApplied, refetchOnMountOrArgChange: true });

  const showWarning = useCallback(
    (detail) => {
      toastRef.current?.show({ severity: "warn", summary: th("common.warning"), detail, life: 3000 });
    },
    [th]
  );

  const applyDateRange = useCallback(
    (updated) => {
      const { startDate, endDate } = updated || {};

      if (!startDate || !endDate) {
        showWarning(th("order.dateRangeRequired"));
        setAppliedDateRange(null);
        return;
      }
      if (startDate > endDate) {
        showWarning(th("order.dateRangeInvalid"));
        setAppliedDateRange(null);
        return;
      }

      setAppliedDateRange({ startDate: formatDateForApi(startDate), endDate: formatDateForApi(endDate) });
      setLazyParams(INITIAL_LAZY_PARAMS);
    },
    [showWarning, th]
  );

  const resetFilters = useCallback(() => {
    setAppliedDateRange(null);
    setColumnFilters({});
    setLazyParams(INITIAL_LAZY_PARAMS);
  }, []);

  const resetColumnFilters = useCallback(() => {
    setFilterResetToken((token) => token + 1);
    setColumnFilters({});
    setLazyParams((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleColumnFiltersChange = useCallback(
    (updated, token) => {
      if (token !== filterResetToken) return;
      setColumnFilters(updated);
      setLazyParams((prev) => ({ ...prev, page: 1 }));
    },
    [filterResetToken]
  );

  const onPage = useCallback((e) => {
    setLazyParams({ page: e.page + 1, rows: e.rows });
  }, []);

  const onSort = useCallback((e) => {
    setSortField(e.sortField || null);
    setSortOrder(e.sortOrder || 0);
    setLazyParams((prev) => ({ ...prev, page: 1 }));
  }, []);

  return {
    toastRef,
    items: data?.rows ?? [],
    totalRecords: data?.totalCount ?? 0,
    isLoading: isLoading || isFetching,
    hasApplied,
    lazyParams,
    sortField,
    sortOrder,
    columnFilters,
    filterResetToken,
    exportParams,
    applyDateRange,
    resetFilters,
    resetColumnFilters,
    handleColumnFiltersChange,
    onPage,
    onSort
  };
};

export default useOrderList;
